import { useState, useEffect } from "react";
import { getRewards } from "../lib/api";
import { Star, Lock, Trophy } from "lucide-react";

export default function RewardsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState(null);

  useEffect(() => {
    getRewards()
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <img src="/LEcoleDeNavi4.png" alt="Chargement" className="h-20 mx-auto mascot-float" />
      </div>
    );
  }

  const stars = data?.stars || 0;
  const rewards = data?.rewards || [];
  const unlocked = rewards.filter((r) => r.unlocked).length;

  return (
    <div data-testid="rewards-page" className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8 page-enter has-bottom-nav lg:pb-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Trophy size={28} className="text-yellow-500" />
          <h1 data-testid="rewards-title" className="font-fredoka text-3xl md:text-4xl font-bold text-slate-800">
            Mon livre de Navi
          </h1>
        </div>
        <p className="font-nunito text-base text-slate-500 mb-4">
          Lis des textes pour gagner des etoiles et debloquer des illustrations !
        </p>

        {/* Stars count */}
        <div data-testid="stars-count" className="inline-flex items-center gap-2 px-5 py-3 bg-yellow-50 border-2 border-yellow-200 rounded-2xl">
          <Star size={24} className="text-yellow-400 fill-yellow-400" />
          <span className="font-fredoka font-bold text-2xl text-yellow-700">{stars}</span>
          <span className="font-nunito font-bold text-sm text-yellow-500">etoile{stars !== 1 ? "s" : ""}</span>
        </div>
        <p className="font-nunito text-sm text-slate-400 mt-2">
          {unlocked}/{rewards.length} illustrations debloquees
        </p>
      </div>

      {/* Rewards grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {rewards.map((reward) => (
          <button
            key={reward.id}
            data-testid={`reward-${reward.id}`}
            onClick={() => reward.unlocked && setSelectedReward(reward)}
            className={`relative rounded-2xl border-2 overflow-hidden transition-all touch-target ${
              reward.unlocked
                ? "border-yellow-300 shadow-md active:scale-95 cursor-pointer"
                : "border-slate-200 opacity-70 cursor-not-allowed"
            }`}
          >
            {/* Image or locked state */}
            <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
              {reward.unlocked ? (
                <img
                  src={reward.image}
                  alt={reward.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-center p-4">
                  <Lock size={32} className="text-slate-300 mx-auto mb-2" />
                  <div className="flex items-center justify-center gap-1">
                    <Star size={14} className="text-yellow-400 fill-yellow-400" />
                    <span className="font-fredoka font-bold text-sm text-slate-400">
                      {reward.stars_needed}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Title */}
            <div className="p-2 bg-white">
              <p className={`font-nunito font-bold text-xs text-center truncate ${
                reward.unlocked ? "text-slate-700" : "text-slate-400"
              }`}>
                {reward.title}
              </p>
            </div>

            {/* Star badge */}
            {reward.unlocked && (
              <div className="absolute top-1.5 right-1.5 w-7 h-7 bg-yellow-400 rounded-full flex items-center justify-center shadow-sm">
                <Star size={14} className="text-white fill-white" />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Full-screen reward viewer */}
      {selectedReward && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setSelectedReward(null)}
        >
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm mx-4 text-center page-enter" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedReward.image}
              alt={selectedReward.title}
              className="w-full max-h-[300px] object-contain rounded-2xl mb-4"
            />
            <h3 className="font-fredoka text-xl font-bold text-slate-800 mb-2">{selectedReward.title}</h3>
            <div className="flex items-center justify-center gap-1 mb-4">
              <Star size={18} className="text-yellow-400 fill-yellow-400" />
              <span className="font-nunito text-sm text-slate-500">
                Debloque a {selectedReward.stars_needed} etoile{selectedReward.stars_needed > 1 ? "s" : ""}
              </span>
            </div>
            <button
              data-testid="close-reward-viewer"
              onClick={() => setSelectedReward(null)}
              className="w-full py-3 bg-orange-500 active:bg-orange-600 text-white font-fredoka font-bold rounded-full shadow-[0_4px_0_rgb(194,65,12)] btn-bouncy touch-target"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
